import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../../stores/auth";
import { api } from "../../lib/api";
import { cn } from "../../lib/utils";
import { invalidateEexData } from "../../lib/eex-invalidate";
import { Td, Th, errMessage, withRecognitionConfirm, type GateExtras } from "./ui";
import { useEexRole } from "./use-eex-role";
import { fmtEur0, fmtPx } from "../../lib/eex-format";
import { matchingImpactAuditSuffix } from "../../features/eex/matching-impact-copy";

// Position & matching — the T view (owner 2026-07-04): buys and sells on two
// sides of a T, each ordered by date; tick lots on both sides and the pairing
// forms LIVE — selected lots per side, weighted average prices, the matched
// quantity (min of the two, capped FIFO-by-date on the bigger side), the
// per-MWh spread, the implied EUR and the booking month(s). Match creates ONE
// pair holding every selected leg; the engine FIFOs within it and books each
// pairing in its closing leg's month. Real×carried stays the declared-close
// flow (one real + one carried lot). Unticked lots deliver at the FSP.
// Shared by the P&L drill-down (EEX-code target) and Positions (product/period).

export type MatchTarget =
  | { code: string; expYear: number; expMonth: number }
  | { product: string; period: string };

interface FillRow {
  side: "B" | "S";
  lots: number;
  price: number;
  date: string;
  tradeId: string | null;
  // The lot's owner — a real×real match must stay within one trader's book.
  trader: string | null;
  cascaded: boolean;
  origin: string | null;
  // Lots already held by pairs / active declared closes — only the remainder
  // is selectable; a fully-committed lot shows dimmed.
  committedLots: number;
  // The lot's owning userId — a trader's Propose is scoped to their own trades
  // (the server enforces the same rule).
  ownerId: string | null;
  // Where each lot BOOKS, attributed from the engine's own lines: close
  // portions (pair / declared / FIFO) by month, plus the delivering remainder.
  books: { month: string; lots: number; via: string; label?: string | null }[];
  deliverLots: number;
  deliverMonths: string[];
}
interface PairMatch {
  // The pair IS the label (label unification) — identified by its name.
  name: string;
  legs: { tradeId: string; side: "B" | "S"; lots: number; price: number; date: string | null }[];
}
interface ParcelPinRow {
  id: string;
  realTradeId: string;
  parcelRootTradeId: string;
  lots: number;
  status: "pending" | "active";
}
interface ProposalRow {
  // A PENDING match proposal touching this leaf — zero engine effect until a
  // company admin approves (stamping the pair label) or rejects.
  id: string;
  name: string;
  note: string | null;
  buyTradeIds: string[];
  sellTradeIds: string[];
  requestedBy: string | null;
  requestedByName: string;
}
interface FillsResponse {
  asOf: string;
  fills: FillRow[];
  leaf: { product: string; period: string } | null;
  leafDelivery: string | null; // the contract's delivery span (fallback label)
  hoursPerLot: number | null; // MWh per lot — scales the € preview
  // Next free name following the house convention (T_HU_Dec25_Match2) — used
  // when the name field is left empty.
  suggestedPairName: string | null;
  pairs: PairMatch[];
  pins: { buyTradeId: string; sellTradeId: string; lots: number; label?: string }[];
  parcelPins: ParcelPinRow[];
  proposals: ProposalRow[];
}

const MONTHS_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const mLabel = (ym: string): string => {
  const [y, m] = ym.split("-");
  return `${MONTHS_ABBR[Number(m) - 1] ?? m}-${y!.slice(2)}`;
};

function eurClass(v: number): string {
  return v > 0 ? "text-posGreen" : v < 0 ? "text-red-500" : "text-muted";
}

const openLots = (f: FillRow): number => Math.max(0, f.lots - (f.committedLots ?? 0));

interface Take {
  tradeId: string;
  lots: number;
  price: number;
  date: string;
}

// FIFO-cap a date-ordered selection at `target` lots: which lots (and how many
// of each) actually match — mirrors the engine's FIFO-within-the-pair.
function fifoTakes(legs: FillRow[], target: number): Take[] {
  const takes: Take[] = [];
  let rem = target;
  for (const l of legs) {
    if (rem <= 0) break;
    const take = Math.min(openLots(l), rem);
    if (take > 0 && l.tradeId) {
      takes.push({ tradeId: l.tradeId, lots: take, price: l.price, date: l.date });
      rem -= take;
    }
  }
  return takes;
}

const wavg = (takes: Take[]): number => {
  const lots = takes.reduce((s, t) => s + t.lots, 0);
  return lots > 0 ? takes.reduce((s, t) => s + t.lots * t.price, 0) / lots : 0;
};

// The booking month(s): pair the two FIFO queues off against each other; each
// pairing books in its LATER leg's month (exactly how the engine dates closes).
function bookingMonths(buyTakes: Take[], sellTakes: Take[]): string[] {
  const b = buyTakes.map((t) => ({ ...t }));
  const s = sellTakes.map((t) => ({ ...t }));
  const months = new Set<string>();
  let bi = 0;
  let si = 0;
  while (bi < b.length && si < s.length) {
    const take = Math.min(b[bi]!.lots, s[si]!.lots);
    const later = b[bi]!.date > s[si]!.date ? b[bi]!.date : s[si]!.date;
    if (later) months.add(later.slice(0, 7));
    b[bi]!.lots -= take;
    s[si]!.lots -= take;
    if (b[bi]!.lots === 0) bi += 1;
    if (s[si]!.lots === 0) si += 1;
  }
  return [...months].sort();
}

interface PreviewImpact {
  tier: "free" | "warning" | "locked";
  affectedCells: { month: string; book: string | null; trader: string | null; state: string; delta: number }[];
  reasons: string[];
}

// Live "what changes vs the FIFO default" — the SAME before/after recognition
// diff the gate runs at Match time, debounced while the selection forms.
// Empty affected cells == the selection merely ratifies the default.
function ImpactPreview({
  payload,
  targetKey,
  adminActs,
}: {
  payload: string | null;
  targetKey: string;
  adminActs: boolean;
}) {
  const [debounced, setDebounced] = useState<string | null>(null);
  useEffect(() => {
    // Long debounce on purpose: each preview is a full recognition replay
    // server-side — fire only once the selection settles.
    const t = setTimeout(() => setDebounced(payload), 800);
    return () => clearTimeout(t);
  }, [payload]);
  const q = useQuery({
    queryKey: ["eex-match-preview", targetKey, debounced],
    enabled: !!debounced,
    staleTime: 60_000,
    queryFn: () =>
      api<{ impact: PreviewImpact; manualConfirmationRequired?: boolean }>(
        `/eex/recognition/match/preview`,
        { method: "POST", body: debounced! }
      ),
  });
  if (!payload) return null;
  if (debounced !== payload || q.isFetching)
    return <div className="text-[11px] text-muted">vs FIFO default: computing…</div>;
  if (q.isError || !q.data) return null;
  const byMonth = new Map<string, number>();
  for (const c of q.data.impact.affectedCells) byMonth.set(c.month, (byMonth.get(c.month) ?? 0) + c.delta);
  const months = [...byMonth.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  const tier = q.data.impact.tier;
  const manualConfirmationRequired = q.data.manualConfirmationRequired !== false;
  const auditSuffix = matchingImpactAuditSuffix({
    tier,
    hasAffectedMonths: months.length > 0,
    adminActs,
    manualConfirmationRequired,
  });
  const automaticAudit =
    tier === "warning" && adminActs && !manualConfirmationRequired && months.length > 0;
  return (
    <div className="text-[11px]">
      <span className="text-muted">vs FIFO default: </span>
      {months.length === 0 ? (
        <span className="text-muted">books nothing new — same as the default</span>
      ) : (
        months.map(([m, d], i) => (
          <span key={m}>
            {i > 0 && <span className="text-muted"> · </span>}
            <span className="text-muted">{mLabel(m)}</span>{" "}
            <span className={cn("tabular-nums font-medium", eurClass(d))}>
              {d >= 0 ? "+" : ""}
              {fmtEur0(d)}
            </span>
          </span>
        ))
      )}
      {tier === "locked" && <span className="text-red-500"> · blocked — a hard-closed month</span>}
      {auditSuffix && (
        <span className={automaticAudit ? "text-muted" : "text-amber-600 dark:text-amber-400"}>
          {auditSuffix}
        </span>
      )}
    </div>
  );
}

export function MatchingPanel({
  target,
  book,
  // string = that trader; null = the no-trader bucket; undefined = ALL traders
  // for this book+contract (the book-grained Positions mount).
  trader,
}: {
  target: MatchTarget;
  book: string | null;
  trader?: string | null;
}) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pairName, setPairName] = useState("");
  const [note, setNote] = useState("");
  // Hovering a pair/proposal/pin below lights up its legs in the T.
  const [hoverLegs, setHoverLegs] = useState<Set<string>>(new Set());
  // Anchor for shift-click range selection, per side — anchored to the TRADE,
  // not a row index, so a refetch re-sorting the list can't shift the range.
  const lastClick = useRef<{ side: "B" | "S"; tradeId: string } | null>(null);
  // Escape clears the ticked lots (ignored while typing in the name/note
  // inputs, where Escape belongs to the field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      setSelected((s) => (s.size > 0 ? new Set<number>() : s));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const params = new URLSearchParams({ book: book ?? "" });
  if (trader !== undefined) params.set("trader", trader ?? "");
  if ("code" in target) {
    params.set("code", target.code);
    params.set("expYear", String(target.expYear));
    params.set("expMonth", String(target.expMonth));
  } else {
    params.set("product", target.product);
    params.set("period", target.period);
  }
  const targetKey =
    "code" in target
      ? `${target.code}|${target.expYear}|${target.expMonth}`
      : `${target.product}|${target.period}`;

  const q = useQuery({
    queryKey: ["eex-recognition-fills", targetKey, book, trader],
    queryFn: () => api<FillsResponse>(`/eex/recognition/fills?${params.toString()}`),
  });
  const invalidate = () => {
    setSelected(new Set());
    setPairName("");
    setNote("");
    // Bust every eex-* query — a hand-listed set here missed the positions
    // tree/leaf keys (["eex-positions-tree"]/["eex-positions-leaf"]), so a match
    // left the drill-down showing pre-match figures within its staleTime.
    invalidateEexData(qc);
  };
  const doMatch = useMutation({
    // Match = stamp the pair label on WHOLE trades. Label edits run the
    // recognition gate, so the confirm flow may add {confirmImpact, reason}.
    mutationFn: (v: { buys: string[]; sells: string[]; name: string } & GateExtras) =>
      api(`/eex/recognition/match`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => {
      setMsg("Matched — books in the closing leg's month.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const doPropose = useMutation({
    // A trader's Match: the same group, but it lands as a PENDING proposal —
    // zero engine effect until a company admin approves. No gate confirm here;
    // the approval act is the confirmation.
    mutationFn: (v: { buys: string[]; sells: string[]; name: string; note?: string }) =>
      api(`/eex/recognition/match-proposals`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: () => {
      setMsg("Proposed — awaiting admin approval.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const actProposal = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" }) =>
      api(`/eex/recognition/match-proposals/${v.id}/${v.action}`, { method: "POST" }),
    onSuccess: () => {
      setMsg("Done.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const withdrawProposal = useMutation({
    mutationFn: (v: { id: string }) =>
      api(`/eex/recognition/match-proposals/${v.id}`, { method: "DELETE" }),
    onSuccess: () => {
      setMsg("Withdrawn.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const unmatch = useMutation({
    mutationFn: (v: { name: string } & GateExtras) =>
      api(`/eex/recognition/unmatch`, { method: "POST", body: JSON.stringify({ ...v, book }) }),
    onSuccess: () => {
      setMsg("Unmatched — the trades go back to open.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const createParcelPin = useMutation({
    mutationFn: (v: { realTradeId: string; parcelRootTradeId: string; lots: number } & GateExtras) =>
      api<{ pending: boolean }>(`/eex/recognition/parcel-pins`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: (r) => {
      setMsg(r.pending ? "Match submitted — awaiting admin confirmation." : "Matched (declared close).");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const createPinBatch = useMutation({
    // M real fills ⇄ N carried parcels, one action: lands as pending pins
    // (trader) or active pins in one transaction (admin confirm flow).
    mutationFn: (
      v: { legs: { realTradeId: string; parcelRootTradeId: string; lots: number }[] } & GateExtras
    ) =>
      api<{ pending: boolean }>(`/eex/recognition/parcel-pins/batch`, { method: "POST", body: JSON.stringify(v) }),
    onSuccess: (r) => {
      setMsg(r.pending ? "Batch submitted — awaiting admin confirmation." : "Matched (declared closes).");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const actParcelPin = useMutation({
    mutationFn: (v: { id: string; action: "approve" | "reject" }) =>
      api(`/eex/recognition/parcel-pins/${v.id}/${v.action}`, { method: "POST" }),
    onSuccess: () => {
      setMsg("Done.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const removeParcelPin = useMutation({
    mutationFn: (v: { id: string; extras: GateExtras }) =>
      api(`/eex/recognition/parcel-pins/${v.id}`, { method: "DELETE", body: JSON.stringify(v.extras) }),
    onSuccess: () => {
      setMsg("Removed.");
      invalidate();
    },
    onError: (e) => setMsg(errMessage(e)),
  });
  const { canWrite, isAdmin: panelIsAdmin } = useEexRole();
  const myUserId = useAuth((s) => s.user?.userId);

  if (q.isLoading) return <div className="text-xs text-muted">loading lots…</div>;
  if (q.error) return <div className="text-xs text-red-500">{(q.error as Error).message}</div>;
  const data = q.data!;
  const fills = data.fills;
  const pairs = data.pairs;
  const parcelPins = data.parcelPins;
  const proposals = data.proposals ?? [];
  const hoursPerLot = data.hoursPerLot;
  // tradeId → pending proposal name, for the "proposed" badge on lots.
  const proposedIn = new Map<string, string>();
  for (const p of proposals)
    for (const tid of [...p.buyTradeIds, ...p.sellTradeIds]) proposedIn.set(tid, p.name);

  if (fills.length === 0)
    return <div className="text-xs text-muted">no fills on this contract for this book/trader.</div>;

  // The T: buys left, sells right, each date-ascending (nulls last).
  const byDate = (a: { f: FillRow }, b: { f: FillRow }) =>
    (a.f.date ?? "9999").localeCompare(b.f.date ?? "9999");
  const indexed = fills.map((f, i) => ({ f, i }));
  const buysAll = indexed.filter((x) => x.f.side === "B").sort(byDate);
  const sellsAll = indexed.filter((x) => x.f.side === "S").sort(byDate);
  const sideTotals = (rows: { f: FillRow }[]) => {
    const lots = rows.reduce((s, x) => s + openLots(x.f), 0);
    const cash = rows.reduce((s, x) => s + openLots(x.f) * x.f.price, 0);
    return { lots, avg: lots > 0 ? cash / lots : null };
  };
  const buyTot = sideTotals(buysAll);
  const sellTot = sideTotals(sellsAll);

  const traders = new Set(fills.filter((f) => !f.cascaded).map((f) => f.trader ?? ""));
  const showTrader = traders.size > 1;

  const picked = [...selected].map((i) => fills[i]).filter(Boolean) as FillRow[];
  const pickedCarried = picked.filter((f) => f.cascaded);
  const pickedReal = picked.filter((f) => !f.cascaded);

  // Mode: declared close (carried parcels on ONE side + opposing real fills —
  // an M×N selection lands as a BATCH of pins, FIFO-allocated across both
  // queues) or the multi-lot real×real flow.
  const carriedMode = pickedCarried.length > 0;
  const carriedSide = pickedCarried[0]?.side;
  const carriedValid =
    carriedMode &&
    pickedCarried.every((c) => c.side === carriedSide) &&
    pickedReal.length >= 1 &&
    pickedReal.every((r) => r.side !== carriedSide);

  const selBuys = buysAll.filter((x) => selected.has(x.i) && !x.f.cascaded).map((x) => x.f);
  const selSells = sellsAll.filter((x) => selected.has(x.i) && !x.f.cascaded).map((x) => x.f);
  const selBuyLots = selBuys.reduce((s, f) => s + openLots(f), 0);
  const selSellLots = selSells.reduce((s, f) => s + openLots(f), 0);

  // Selection readout (informational, independent of the matching math below):
  // per-side ticked size + VWAP over ALL ticked lots — carried parcels INCLUDED.
  // A delivery leaf is often entirely carried (the real fills live at the parent
  // contract and cascade in), so the real-only sel* sets above are empty there;
  // the trader still wants "how much did I tick on this side, at what basis".
  // Each carried parcel's `price` is its inherited basis B (the Price column).
  const selBuysDisp = buysAll.filter((x) => selected.has(x.i)).map((x) => x.f);
  const selSellsDisp = sellsAll.filter((x) => selected.has(x.i)).map((x) => x.f);
  const dispSide = (rows: FillRow[]) => {
    const lots = rows.reduce((s, f) => s + openLots(f), 0);
    const cash = rows.reduce((s, f) => s + openLots(f) * f.price, 0);
    return { lots, avg: lots > 0 ? cash / lots : null };
  };
  const selBuyDisp = dispSide(selBuysDisp);
  const selSellDisp = dispSide(selSellsDisp);
  const matched = carriedMode ? 0 : Math.min(selBuyLots, selSellLots);
  const buyTakes = matched > 0 ? fifoTakes(selBuys, matched) : [];
  const sellTakes = matched > 0 ? fifoTakes(selSells, matched) : [];
  const wavgBuy = wavg(buyTakes);
  const wavgSell = wavg(sellTakes);
  const delta = matched > 0 ? wavgSell - wavgBuy : 0;
  const eur = matched > 0 && hoursPerLot != null ? delta * matched * hoursPerLot : null;
  const months = matched > 0 ? bookingMonths(buyTakes, sellTakes) : [];
  const crossTrader =
    !carriedMode && new Set([...selBuys, ...selSells].map((f) => f.trader ?? "")).size > 1;

  // FIFO×FIFO: walk both queues oldest-first, allocating min(remaining) at each
  // meet — every surviving slice becomes one pin (merged per real×parcel pair).
  const pinLegs = carriedValid
    ? (() => {
        const parcels = pickedCarried
          .slice()
          .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
          .map((p) => ({ id: p.tradeId!, left: p.lots }));
        const reals = pickedReal
          .slice()
          .sort((a, b) => (a.date ?? "").localeCompare(b.date ?? ""))
          .map((r) => ({ id: r.tradeId!, left: openLots(r) }));
        const alloc = new Map<string, { realTradeId: string; parcelRootTradeId: string; lots: number }>();
        let pi = 0;
        let ri = 0;
        while (pi < parcels.length && ri < reals.length) {
          const p = parcels[pi]!;
          const r = reals[ri]!;
          const take = Math.min(p.left, r.left);
          if (take > 0) {
            const k = `${r.id}|${p.id}`;
            const e = alloc.get(k) ?? { realTradeId: r.id, parcelRootTradeId: p.id, lots: 0 };
            e.lots += take;
            alloc.set(k, e);
            p.left -= take;
            r.left -= take;
          }
          if (p.left === 0) pi += 1;
          if (r.left === 0) ri += 1;
        }
        return [...alloc.values()];
      })()
    : [];
  const carriedLots = pinLegs.reduce((s, l) => s + l.lots, 0);
  // A trader's real×real Match lands as a PROPOSAL (admin approves); admins
  // stamp the label directly. Declared closes already carry their own
  // pending-until-approved flow.
  const proposeMode = !carriedMode && canWrite && !panelIsAdmin;
  // The server's own-trades rule, mirrored: a trader proposes on THEIR lots only.
  const notMine =
    proposeMode && [...selBuys, ...selSells].some((f) => (f.ownerId ?? null) !== (myUserId ?? null));
  // The batch/preview endpoints cap at 100 legs; a huge M×N selection would
  // 400. Block it here with a clear hint instead.
  const tooManyLegs = carriedMode && pinLegs.length > 100;
  const canMatch = carriedMode
    ? carriedValid && carriedLots > 0 && !tooManyLegs && !createParcelPin.isPending && !createPinBatch.isPending
    : matched > 0 && !crossTrader && !notMine && !doMatch.isPending && !doPropose.isPending;

  let hint = "tick lots on both sides — the pairing forms live below";
  if (carriedMode && !carriedValid)
    hint = "a declared close pairs carried parcels (one side) with opposing real fills — adjust the selection";
  else if (tooManyLegs) hint = "too many pairings in one batch (max 100) — split the selection";
  else if (carriedMode && carriedValid)
    hint = `declares ${carriedLots} lot(s) — ${pickedReal.length} fill(s) ⇄ ${pickedCarried.length} parcel(s) — books in the real fills' month(s)${panelIsAdmin ? "" : " once an admin confirms"}`;
  else if (crossTrader) hint = "selected lots belong to different traders — a match must stay within one trader's book";
  else if (notMine) hint = "you can only propose matches on your own trades";
  else if (selected.size > 0 && matched === 0) hint = "select at least one buy AND one sell lot";
  else if (proposeMode && matched > 0) hint = "proposes the pair — nothing books until an admin approves";

  const isSelectable = (f: FillRow): boolean =>
    !!f.tradeId && openLots(f) > 0 && (f.cascaded || (f.committedLots ?? 0) === 0);

  // Balance: auto-tick the oldest opposing uncommitted lots (same trader; own
  // lots only in propose mode) until the sides equalize. Whole-trade grain
  // means no overshoot — a fill that would overshoot is skipped and smaller,
  // later fills are still considered.
  const balance = () => {
    const buySideTicked = selBuyLots > 0;
    const target = buySideTicked ? selBuyLots : selSellLots;
    const source = buySideTicked ? sellsAll : buysAll;
    const tickedTraders = new Set(pickedReal.map((f) => f.trader ?? ""));
    let have = buySideTicked ? selSellLots : selBuyLots;
    const add: number[] = [];
    for (const { f, i } of source) {
      if (have >= target) break;
      if (selected.has(i) || f.cascaded || !isSelectable(f)) continue;
      if (tickedTraders.size === 1 && !tickedTraders.has(f.trader ?? "")) continue;
      if (proposeMode && (f.ownerId ?? null) !== (myUserId ?? null)) continue;
      if (have + openLots(f) > target) continue;
      add.push(i);
      have += openLots(f);
    }
    if (add.length > 0) setSelected((s) => new Set([...s, ...add]));
  };

  // Live impact-preview payload: a real×real group or a declared-close batch.
  // Only for selections the user could actually act on — the endpoint is
  // write-gated and enforces ownership, so don't fire doomed calls.
  const ownsAllReals =
    panelIsAdmin || pickedReal.every((f) => (f.ownerId ?? null) === (myUserId ?? null));
  const previewPayload = !canWrite
    ? null
    : !carriedMode && matched > 0 && !crossTrader && !notMine
      ? JSON.stringify({ buys: selBuys.map((f) => f.tradeId), sells: selSells.map((f) => f.tradeId) })
      : carriedMode && carriedValid && pinLegs.length > 0 && pinLegs.length <= 100 && ownsAllReals
        ? JSON.stringify({ pins: pinLegs })
        : null;

  const onMatch = () => {
    if (!canMatch) return;
    if (carriedMode) {
      if (pinLegs.length === 1) {
        const leg = pinLegs[0]!;
        void withRecognitionConfirm((x) =>
          createParcelPin.mutateAsync({
            realTradeId: leg.realTradeId,
            parcelRootTradeId: leg.parcelRootTradeId,
            lots: leg.lots,
            ...x,
          })
        ).catch(() => {});
      } else {
        void withRecognitionConfirm((x) => createPinBatch.mutateAsync({ legs: pinLegs, ...x })).catch(
          () => {}
        );
      }
    } else if (proposeMode) {
      const name = pairName.trim() || data.suggestedPairName || "pair";
      doPropose.mutate({
        buys: selBuys.map((f) => f.tradeId!),
        sells: selSells.map((f) => f.tradeId!),
        name,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    } else {
      // Whole trades: every selected trade joins the pair; the excess side's
      // remainder stays in the group and delivers.
      const name = pairName.trim() || data.suggestedPairName || "pair";
      void withRecognitionConfirm((x) =>
        doMatch.mutateAsync({
          buys: selBuys.map((f) => f.tradeId!),
          sells: selSells.map((f) => f.tradeId!),
          name,
          ...x,
        })
      ).catch(() => {});
    }
  };

  const pairRealized = (m: PairMatch): number | null => {
    let bl = 0, bc = 0, sl = 0, sc = 0;
    for (const l of m.legs) {
      if (l.side === "B") { bl += l.lots; bc += l.lots * l.price; }
      else { sl += l.lots; sc += l.lots * l.price; }
    }
    if (bl === 0 || sl === 0) return null;
    return sc / sl - bc / bl;
  };
  // A pair's matched lots + booking month(s): the SAME date-ordered FIFO the
  // engine runs within the pair (each pairing books in its later leg's month).
  const pairBooking = (m: PairMatch): { lots: number; months: string[] } => {
    const toTakes = (side: "B" | "S") =>
      m.legs
        .filter((l) => l.side === side && l.date)
        .sort((a, b) => a.date!.localeCompare(b.date!))
        .map((l) => ({ tradeId: l.tradeId, lots: l.lots, price: l.price, date: l.date! }));
    const b = toTakes("B");
    const s = toTakes("S");
    const lots = Math.min(
      b.reduce((x, t) => x + t.lots, 0),
      s.reduce((x, t) => x + t.lots, 0)
    );
    return { lots, months: lots > 0 ? bookingMonths(b, s) : [] };
  };
  const legLabel = (id: string) => {
    const f = fills.find((x) => x.tradeId === id);
    return f ? `${f.side === "B" ? "buy" : "sell"} ${f.lots}@${fmtPx(f.price, 2)}` : id.slice(0, 8);
  };

  const toggle = (i: number) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(i)) n.delete(i);
      else n.add(i);
      return n;
    });

  // Where a lot's P&L books — from the engine's own lines, so it always agrees
  // with the P&L page: "1→Nov-25 (T_HU..)" pair close, "(FIFO)" default close,
  // "(FSP)" delivery of the remainder.
  const booksLabel = (f: FillRow): string => {
    const parts: string[] = [];
    for (const b of f.books ?? []) {
      const via = b.via === "schedule" ? "scheduled allocation" : b.via === "pair" ? b.label ?? "pair" : b.via === "declared" ? "declared" : "FIFO";
      parts.push(`${b.lots}→${mLabel(b.month)} (${via})`);
    }
    const dl = f.deliverLots ?? 0;
    if (dl > 0) {
      const when =
        (f.deliverMonths ?? []).length > 0
          ? f.deliverMonths.map(mLabel).join("/")
          : data.leafDelivery ?? "delivery";
      parts.push(`${dl}→${when} (FSP)`);
    }
    return parts.join(" · ") || "—";
  };

  // Click with shift held selects the whole range since the last click on the
  // same side (selectable rows only).
  const clickRow = (rows: { f: FillRow; i: number }[], pos: number, side: "B" | "S", shiftKey: boolean) => {
    const row = rows[pos];
    if (!row || !isSelectable(row.f)) return;
    const anchorPos =
      shiftKey && lastClick.current && lastClick.current.side === side
        ? rows.findIndex((x) => x.f.tradeId === lastClick.current!.tradeId)
        : -1;
    if (anchorPos >= 0) {
      const [a, b] = [Math.min(anchorPos, pos), Math.max(anchorPos, pos)];
      setSelected((s) => {
        const n = new Set(s);
        for (let p = a; p <= b; p++) {
          const r = rows[p];
          if (r && isSelectable(r.f)) n.add(r.i);
        }
        return n;
      });
    } else {
      toggle(row.i);
    }
    if (row.f.tradeId) lastClick.current = { side, tradeId: row.f.tradeId };
  };

  // Rendered as a plain function (NOT a nested component) so rows keep their
  // DOM identity across re-renders — keyboard focus survives toggling.
  const renderSide = (rows: { f: FillRow; i: number }[], side: "B" | "S") => {
    // Select-all covers REAL fills only — sweeping carried parcels in would
    // flip the panel into (an almost always invalid) declared-close mode.
    const selectableRows = rows.filter((x) => isSelectable(x.f) && !x.f.cascaded);
    const allOn = selectableRows.length > 0 && selectableRows.every((x) => selected.has(x.i));
    return (
    <table className="border-collapse w-full">
      <thead>
        <tr className="text-muted border-b border-line/50">
          <Th>
            <input
              type="checkbox"
              checked={allOn}
              disabled={selectableRows.length === 0}
              title="select all open real fills on this side"
              onChange={() =>
                setSelected((s) => {
                  const n = new Set(s);
                  for (const x of selectableRows) {
                    if (allOn) n.delete(x.i);
                    else n.add(x.i);
                  }
                  return n;
                })
              }
            />
          </Th>
          <Th className="text-right">Lots</Th>
          <Th className="text-right">Price</Th>
          <Th>Date</Th>
          {showTrader && <Th>Trader</Th>}
          <Th>Source</Th>
          <Th>Books</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map(({ f, i }, pos) => {
          const on = selected.has(i);
          const open = openLots(f);
          // Matching is whole-trade: a real fill partially held by a declared
          // close can't join a pair — only fully-uncommitted trades select.
          const selectable = isSelectable(f);
          return (
            <tr
              key={i}
              tabIndex={selectable ? 0 : -1}
              className={cn(
                "border-b border-line/30 focus:outline-none focus:bg-line/30",
                selectable ? "cursor-pointer" : "opacity-50",
                on && "bg-line/40",
                f.tradeId && hoverLegs.has(f.tradeId) && "bg-sky-500/10"
              )}
              onMouseDown={(e) => e.shiftKey && e.preventDefault()}
              onClick={(e) => clickRow(rows, pos, side, e.shiftKey)}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.preventDefault();
                  clickRow(rows, pos, side, e.shiftKey);
                } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                  e.preventDefault();
                  let el: Element | null =
                    e.key === "ArrowDown"
                      ? e.currentTarget.nextElementSibling
                      : e.currentTarget.previousElementSibling;
                  while (el && (el as HTMLElement).tabIndex < 0)
                    el = e.key === "ArrowDown" ? el.nextElementSibling : el.previousElementSibling;
                  (el as HTMLElement | null)?.focus();
                }
              }}
            >
              <Td>
                <input type="checkbox" checked={on} disabled={!selectable} readOnly />
              </Td>
              <Td className="text-right tabular-nums">
                {open}
                {open !== f.lots && <span className="text-muted text-[10px]">/{f.lots}</span>}
              </Td>
              <Td className="text-right tabular-nums">{fmtPx(f.price, 2)}</Td>
              <Td className="whitespace-nowrap">{f.date}</Td>
              {showTrader && (
                <Td className="text-muted">{f.cascaded ? "—" : f.trader ?? "(unassigned)"}</Td>
              )}
              <Td className={f.cascaded ? "text-sky-600 dark:text-sky-400" : "text-muted"}>
                {f.cascaded ? `carried ← ${f.origin ?? "cascade"}` : "traded"}
                {!f.cascaded && f.tradeId && proposedIn.has(f.tradeId) && (
                  <span
                    className="ml-1 px-1 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-600 dark:text-amber-400"
                    title={`in pending proposal "${proposedIn.get(f.tradeId)}"`}
                  >
                    proposed
                  </span>
                )}
              </Td>
              <Td className="text-[11px] text-muted whitespace-nowrap">{booksLabel(f)}</Td>
            </tr>
          );
        })}
        {rows.length === 0 && (
          <tr>
            <Td className="text-muted italic" colSpan={showTrader ? 7 : 6}>
              none
            </Td>
          </tr>
        )}
      </tbody>
    </table>
    );
  };

  return (
    <div className="border border-line/60 rounded p-2 space-y-2 text-xs">
      <div className="text-[10px] uppercase tracking-wide text-muted">
        Position &amp; matching — tick lots on both sides of the T; unticked lots deliver at the FSP
      </div>

      <div className="grid grid-cols-2 gap-0">
        <div className="border-r border-line/60 pr-2">
          <div className="flex justify-between items-baseline border-b border-line pb-1 mb-1">
            <span className="font-medium text-posGreen">Buys · {buyTot.lots} open</span>
            <span className="text-muted tabular-nums">
              {buyTot.avg != null ? `avg ${fmtPx(buyTot.avg, 2)}` : ""}
            </span>
          </div>
          {renderSide(buysAll, "B")}
        </div>
        <div className="pl-2">
          <div className="flex justify-between items-baseline border-b border-line pb-1 mb-1">
            <span className="font-medium text-red-500">Sells · {sellTot.lots} open</span>
            <span className="text-muted tabular-nums">
              {sellTot.avg != null ? `avg ${fmtPx(sellTot.avg, 2)}` : ""}
            </span>
          </div>
          {renderSide(sellsAll, "S")}
        </div>
      </div>

      {selBuyDisp.lots + selSellDisp.lots > 0 && (
        // Always-on selection readout: per-side ticked size + VWAP, live, for one
        // side or both, real fills OR carried parcels. Independent of whether a
        // pairing can form (the outcome strip below covers that).
        <div className="border border-line rounded px-3 py-1.5 flex items-center gap-3 flex-wrap bg-line/20">
          {selBuyDisp.lots > 0 && (
            <span>
              <b className="font-medium">{selBuyDisp.lots}</b> buy
              {selBuyDisp.avg != null && (
                <>
                  {" @ "}
                  <span className="tabular-nums">{fmtPx(selBuyDisp.avg, 2)}</span>
                </>
              )}
              {hoursPerLot != null && (
                <span className="text-muted tabular-nums">
                  {" · "}
                  {(selBuyDisp.lots * hoursPerLot).toLocaleString("en-US")} MWh
                </span>
              )}
            </span>
          )}
          {selSellDisp.lots > 0 && (
            <span>
              <b className="font-medium">{selSellDisp.lots}</b> sell
              {selSellDisp.avg != null && (
                <>
                  {" @ "}
                  <span className="tabular-nums">{fmtPx(selSellDisp.avg, 2)}</span>
                </>
              )}
              {hoursPerLot != null && (
                <span className="text-muted tabular-nums">
                  {" · "}
                  {(selSellDisp.lots * hoursPerLot).toLocaleString("en-US")} MWh
                </span>
              )}
            </span>
          )}
          <span className="text-muted">
            {(selBuyDisp.lots > 0) !== (selSellDisp.lots > 0)
              ? "tick the other side to form the pairing · Esc clears"
              : "Esc clears"}
          </span>
        </div>
      )}

      {matched > 0 && !carriedMode && !crossTrader && (
        <div className="border border-line rounded px-3 py-1.5 flex items-center gap-3 flex-wrap bg-line/20">
          <span>
            <span className="text-muted">⇄ </span>
            matches <b className="font-medium">{matched}</b>
            {selBuyLots !== selSellLots && (
              <span className="text-muted">
                {" "}
                (the other {Math.abs(selBuyLots - selSellLots)}{" "}
                {selBuyLots > selSellLots ? "buy" : "sell"} lot
                {Math.abs(selBuyLots - selSellLots) === 1 ? "" : "s"} stay in the pair and deliver)
              </span>
            )}
          </span>
          <span className={cn("tabular-nums font-medium", eurClass(delta))}>
            {delta >= 0 ? "+" : ""}
            {fmtPx(delta, 2)}/MWh
            {eur != null && (
              <>
                {" "}
                ≈ {eur >= 0 ? "+" : ""}
                {fmtEur0(eur)}
              </>
            )}
          </span>
          {months.length > 0 && (
            <span className="text-muted">
              books {months.join(", ")}
            </span>
          )}
        </div>
      )}

      <ImpactPreview payload={previewPayload} targetKey={targetKey} adminActs={panelIsAdmin} />

      <div className="flex items-center gap-2 flex-wrap">
        {!carriedMode && (selBuyLots > 0) !== (selSellLots > 0) && (
          <button
            className="border border-line rounded px-2 py-0.5 text-muted hover:text-fg"
            onClick={balance}
            title="auto-tick the oldest opposing lots up to the same size"
          >
            Balance ⇄
          </button>
        )}
        {!carriedMode && (
          <input
            className="bg-transparent border border-line rounded px-2 py-0.5 text-xs w-52"
            placeholder={data.suggestedPairName ?? "pair name (optional)"}
            title="leave empty to use the suggested name"
            value={pairName}
            onChange={(e) => setPairName(e.target.value)}
          />
        )}
        {proposeMode && (
          <input
            className="bg-transparent border border-line rounded px-2 py-0.5 text-xs w-56"
            placeholder="note for the approver (optional)"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}
        <button
          className="border border-posGreen/50 text-posGreen rounded px-2 py-0.5 font-medium hover:bg-posGreen/10 disabled:opacity-40 disabled:border-line disabled:text-muted disabled:hover:bg-transparent disabled:cursor-not-allowed"
          disabled={!canMatch}
          onClick={onMatch}
        >
          {carriedMode
            ? `Match ${carriedValid ? carriedLots : ""} → close`
            : proposeMode
              ? `Propose ${matched > 0 ? matched : ""} → for approval`
              : `Match ${matched > 0 ? matched : ""} → close`}
        </button>
        <span className="text-[11px] text-muted">{hint}</span>
      </div>

      {proposals.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted">Proposed — awaiting admin approval</div>
          {proposals.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 flex-wrap"
              onMouseEnter={() => setHoverLegs(new Set([...p.buyTradeIds, ...p.sellTradeIds]))}
              onMouseLeave={() => setHoverLegs(new Set())}
            >
              <span className="px-1.5 py-0.5 rounded text-[10px] uppercase bg-amber-500/15 text-amber-600 dark:text-amber-400">
                pending
              </span>
              <span className="font-medium">{p.name}</span>
              <span className="text-muted">
                {[...p.buyTradeIds, ...p.sellTradeIds].map(legLabel).join("  ⇄  ")}
              </span>
              <span className="text-muted">by {p.requestedByName}</span>
              {p.note && <span className="text-muted italic">— {p.note}</span>}
              {panelIsAdmin ? (
                <>
                  <button
                    className="text-posGreen hover:underline"
                    onClick={() => actProposal.mutate({ id: p.id, action: "approve" })}
                  >
                    approve
                  </button>
                  <button
                    className="text-red-500 hover:underline"
                    onClick={() => actProposal.mutate({ id: p.id, action: "reject" })}
                  >
                    reject
                  </button>
                </>
              ) : (
                canWrite &&
                p.requestedBy === myUserId && (
                  <button className="text-muted hover:underline" onClick={() => withdrawProposal.mutate({ id: p.id })}>
                    withdraw
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}

      {pairs.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted">Matched — pairs</div>
          {pairs.map((p) => {
            const r = pairRealized(p);
            const booking = pairBooking(p);
            const pairEur =
              r != null && hoursPerLot != null && booking.lots > 0 ? r * booking.lots * hoursPerLot : null;
            return (
              <div
                key={p.name}
                className="flex items-center gap-2 flex-wrap"
                onMouseEnter={() => setHoverLegs(new Set(p.legs.map((l) => l.tradeId)))}
                onMouseLeave={() => setHoverLegs(new Set())}
              >
                <span className="font-medium">{p.name}</span>
                <span className="text-muted">
                  {p.legs.map((l) => `${l.side === "B" ? "buy" : "sell"} ${l.lots}@${fmtPx(l.price, 2)}`).join("  ⇄  ")}
                </span>
                {r != null && (
                  <span className={eurClass(r)}>
                    {r >= 0 ? "+" : ""}
                    {fmtPx(r, 2)}/MWh
                    {pairEur != null && (
                      <>
                        {" "}
                        ≈ {pairEur >= 0 ? "+" : ""}
                        {fmtEur0(pairEur)}
                      </>
                    )}
                  </span>
                )}
                {booking.months.length > 0 && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded bg-line/40">
                    books {booking.months.join(", ")}
                  </span>
                )}
                <button
                  className="text-red-500 hover:underline"
                  onClick={() =>
                    void withRecognitionConfirm((x) => unmatch.mutateAsync({ name: p.name, ...x })).catch(() => {})
                  }
                >
                  unmatch
                </button>
              </div>
            );
          })}
        </div>
      )}

      {parcelPins.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] uppercase tracking-wide text-muted">Matched — declared closes vs carried</div>
          {parcelPins.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-2 flex-wrap"
              onMouseEnter={() => setHoverLegs(new Set([p.realTradeId, p.parcelRootTradeId]))}
              onMouseLeave={() => setHoverLegs(new Set())}
            >
              <span
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] uppercase",
                  p.status === "active"
                    ? "bg-posGreen/15 text-posGreen"
                    : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                )}
              >
                {p.status}
              </span>
              <span>
                {p.lots} lot(s): real {legLabel(p.realTradeId)} ⇄ carried {legLabel(p.parcelRootTradeId)}
              </span>
              {p.status === "pending" && panelIsAdmin && (
                <>
                  <button className="text-posGreen hover:underline" onClick={() => actParcelPin.mutate({ id: p.id, action: "approve" })}>
                    approve
                  </button>
                  <button className="text-red-500 hover:underline" onClick={() => actParcelPin.mutate({ id: p.id, action: "reject" })}>
                    reject
                  </button>
                </>
              )}
              {(p.status === "pending" || panelIsAdmin) && (
                <button
                  className="text-muted hover:underline"
                  onClick={() =>
                    void withRecognitionConfirm((x) => removeParcelPin.mutateAsync({ id: p.id, extras: x })).catch(() => {})
                  }
                >
                  remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="text-[10px] text-muted border-t border-line/40 pt-1">
        traded = your fill · <span className="text-sky-600 dark:text-sky-400">carried</span> = inherited from a
        cascade, at its own basis · lots dimmed when fully matched · unmatched lots deliver at the FSP
      </div>
      {msg && <div className="text-muted">{msg}</div>}
    </div>
  );
}
